<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { RouterLink, useRoute, useRouter, type RouteRecordName } from 'vue-router';
import { useTranslation } from 'i18next-vue';

import type { MenuItem } from 'primevue/menuitem';
import type Menu from 'primevue/menu';

import { BurgerMenuIcon, SupportIcon } from '@peanuds/ui';
import { authRouteNames, features, useFeatureFlag, useHubspotChat } from '@peanuds/app-shell';
import {
  BATCH_COLLECTION_DRAFT_REVIEW_ROUTE_NAME,
  BATCH_TRANSACTION_DETAILS_ROUTE_NAME,
  BATCH_TRANSACTIONS_ROUTE_NAME,
} from '@peanuds/batch-collections';
import { SEND_MONEY_ROUTE_NAME } from '@peanuds/money';
import { NotificationsNavItem } from '@peanuds/modules';

import AppAccountSwitcher from './AppAccountSwitcher.vue';
import BebawaLogo from '../assets/logos/logo.svg?component';

interface Props {
  onLogout?: () => void;
  paymentsApprovalCount?: number;
  paymentsApprovalHasError?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  onLogout: () => {},
  paymentsApprovalCount: 0,
  paymentsApprovalHasError: false,
});

const router = useRouter();
const route = useRoute();
const { t } = useTranslation();
const isBatchCollectionsEnabled = useFeatureFlag(features.batchCollections);
const isPaymentsApprovalEnabled = useFeatureFlag(features.paymentsApproval);

const NAVBAR_COLLAPSE_BREAKPOINT = '960px';
const NAVBAR_COLLAPSE_MEDIA_QUERY = `(max-width: ${NAVBAR_COLLAPSE_BREAKPOINT})`;

const paymentsApprovalDigitCount = computed(() => props.paymentsApprovalCount.toString().length);

type MenuInstance = InstanceType<typeof Menu>;
type MainNavigationItem = {
  icon: string;
  key: string;
  label: string;
  routeName: RouteRecordName;
  burgerLabel?: string;
};

const { isChatAvailable, openChat } = useHubspotChat();

const navigateToRoute = (routeName: RouteRecordName) => {
  router.push({ name: routeName });
};

const paymentsApprovalBurgerMenuLabel = computed(() => {
  if (props.paymentsApprovalHasError) {
    return `${t('nav.payments_approval')} (!)`;
  }

  if (props.paymentsApprovalCount > 0) {
    return `${t('nav.payments_approval')} (${props.paymentsApprovalCount})`;
  }

  return t('nav.payments_approval');
});

const mainNavigationItems = computed<MainNavigationItem[]>(() => [
  {
    label: t('nav.overview'),
    key: 'dashboard',
    routeName: 'dashboard',
    icon: 'pi pi-home',
  },
  {
    label: t('nav.send_money'),
    key: 'send-money',
    routeName: SEND_MONEY_ROUTE_NAME,
    icon: 'pi pi-arrow-up-right',
  },
  ...(isBatchCollectionsEnabled.value
    ? [
        {
          label: t('nav.batch_collections'),
          key: 'batch-collections',
          routeName: BATCH_TRANSACTIONS_ROUTE_NAME,
          icon: 'pi pi-list',
        },
      ]
    : []),
  {
    label: t('nav.accounts'),
    key: 'accounts',
    routeName: 'accounts',
    icon: 'pi pi-wallet',
  },
  {
    label: t('nav.analytics'),
    key: 'analytics',
    routeName: 'analytics',
    icon: 'pi pi-chart-line',
  },
  ...(isPaymentsApprovalEnabled.value
    ? [
        {
          label: t('nav.payments_approval'),
          burgerLabel: paymentsApprovalBurgerMenuLabel.value,
          key: 'payments-approval',
          routeName: 'payments-approval',
          icon: 'pi pi-check-circle',
        },
      ]
    : []),
]);

const isNavbarCollapsed = ref(false);
let navbarMediaQueryList: MediaQueryList | null = null;

const syncNavbarCollapsed = () => {
  if (!navbarMediaQueryList) {
    return;
  }

  isNavbarCollapsed.value = navbarMediaQueryList.matches;
};

onMounted(() => {
  navbarMediaQueryList = window.matchMedia(NAVBAR_COLLAPSE_MEDIA_QUERY);
  syncNavbarCollapsed();
  navbarMediaQueryList.addEventListener('change', syncNavbarCollapsed);
});

onUnmounted(() => {
  navbarMediaQueryList?.removeEventListener('change', syncNavbarCollapsed);
  navbarMediaQueryList = null;
});

const navbarMenuItems = computed<MenuItem[]>(() =>
  mainNavigationItems.value.map(({ key, label, routeName }) => ({
    label,
    key,
    command: () => {
      navigateToRoute(routeName);
    },
  })),
);

const burgerMenuItems = computed<MenuItem[]>(() => [
  {
    label: t('nav.add_new_profile'),
    key: authRouteNames.createOrganization,
    class: 'app-nav__apply-account-item',
    command: () => {
      router.push({ name: authRouteNames.createOrganization });
    },
  },
  { separator: true },
  ...(isNavbarCollapsed.value
    ? [
        {
          label: t('nav.main'),
          items: mainNavigationItems.value.map(({ burgerLabel, icon, key, label, routeName }) => ({
            label: burgerLabel ?? label,
            key,
            icon,
            command: () => {
              navigateToRoute(routeName);
            },
          })),
        },
        { separator: true },
      ]
    : []),
  {
    label: t('nav.logout'),
    key: 'logout',
    icon: 'pi pi-sign-out',
    command: () => props.onLogout(),
  },
]);

const burgerMenu = ref<MenuInstance | null>();

const routeNameToMenuKey: Record<string, string> = {
  dashboard: 'dashboard',
  [SEND_MONEY_ROUTE_NAME]: 'send-money',
  'send-money-flow': 'send-money',
  'new-recipient': 'send-money',
  'existing-contact': 'send-money',
  myself: 'send-money',
  'payment-processed': 'send-money',
  [BATCH_TRANSACTIONS_ROUTE_NAME]: 'batch-collections',
  [BATCH_TRANSACTION_DETAILS_ROUTE_NAME]: 'batch-collections',
  [BATCH_COLLECTION_DRAFT_REVIEW_ROUTE_NAME]: 'batch-collections',
  accounts: 'accounts',
  'account-review': 'accounts',
  'open-account': 'accounts',
  analytics: 'analytics',
  'payments-approval': 'payments-approval',
  settings: 'settings',
  'settings-company-details': 'settings',
  'settings-login-activity': 'settings',
  'settings-account-safety': 'settings',
  'settings-user-control': 'settings',
};

const resolveMenuKey = (routeName?: RouteRecordName | null) => {
  if (typeof routeName !== 'string') {
    return null;
  }

  return routeNameToMenuKey[routeName] ?? null;
};

const activeItemKey = computed(() => resolveMenuKey(route.name));

const toggleBurgerMenu = (event: MouseEvent) => {
  burgerMenu.value?.toggle(event);
};

const hideBurgerMenu = () => {
  burgerMenu.value?.hide();
};
</script>

<template>
  <Menubar :model="navbarMenuItems" :breakpoint="NAVBAR_COLLAPSE_BREAKPOINT">
    <template #start>
      <RouterLink to="/" class="navbar-logo">
        <BebawaLogo />
      </RouterLink>
    </template>

    <template #item="{ item, props: itemProps }">
      <div
        v-bind="itemProps.action"
        class="p-menuitem-link relative"
        :data-testid="`app-nav-main-${item.key}`"
        :class="{ 'app-nav__menu-item--active': activeItemKey === item.key }"
      >
        <span class="p-menuitem-text">{{ item.label }}</span>
        <Badge
          v-if="
            item.key === 'payments-approval' &&
            (paymentsApprovalHasError || paymentsApprovalCount > 0)
          "
          :value="paymentsApprovalHasError ? '!' : paymentsApprovalCount"
          :severity="paymentsApprovalHasError ? 'warn' : 'danger'"
          class="navbar-badge"
          :class="{
            'navbar-badge--two-digits':
              !paymentsApprovalHasError && paymentsApprovalDigitCount === 2,
            'navbar-badge--three-digits':
              !paymentsApprovalHasError && paymentsApprovalDigitCount >= 3,
          }"
        />
      </div>
    </template>

    <template #end>
      <div class="navbar-end">
        <Button
          v-if="isChatAvailable"
          severity="secondary"
          rounded
          :aria-label="t('nav.support')"
          @click="openChat"
        >
          <template #icon>
            <SupportIcon />
          </template>
        </Button>

        <NotificationsNavItem />

        <Button
          rounded
          style="margin-left: 0.5rem"
          type="button"
          icon="pi pi-align-justify"
          data-testid="app-nav-menu-toggle"
          @click="toggleBurgerMenu"
          aria-haspopup="true"
          aria-controls="burger_menu"
          severity="secondary"
        >
          <template #icon>
            <BurgerMenuIcon />
          </template>
        </Button>

        <Menu ref="burgerMenu" id="burger_menu" :model="burgerMenuItems" :popup="true">
          <template #start>
            <AppAccountSwitcher @close="hideBurgerMenu" />
          </template>
        </Menu>
      </div>
    </template>
  </Menubar>
</template>

<style lang="scss">
.navbar-logo {
  padding: 0;

  svg {
    width: 77px;
  }
}

.navbar-end {
  display: flex;
  gap: 0.5rem;
}

#burger_menu {
  min-width: 218px;
  border-color: var(--p-content-border-color);
  border-radius: var(--radius-xl);
}

#burger_menu .p-menu-item-icon {
  color: var(--p-surface-400);
}

#burger_menu .p-menu-start {
  padding: 0;
}

#burger_menu .p-menu-start:empty {
  display: none;
}

#burger_menu .app-nav__apply-account-item .p-menu-item-content {
  margin: $static-space-2 0;
}

#burger_menu .p-menu-item-link {
  box-sizing: border-box;
  height: calc($static-space-8 + $static-space-1);
  min-height: calc($static-space-8 + $static-space-1);
}

#burger_menu .p-menu-separator {
  margin-inline: calc(-1 * #{$static-space-1});
}

#burger_menu .p-menu-item:last-child {
  margin-top: 1.25px;
}

.p-menubar-mobile .p-menubar-button {
  display: none !important;
}
</style>

<style lang="scss" scoped>
.p-menubar {
  max-width: 1600px;
  margin: 0 auto;
  padding: 7px 10.5px;
  gap: $static-space-3;
}

.navbar-end {
  :deep(.p-button) {
    width: 2rem;
    height: 2rem;
  }
}

.navbar-badge {
  position: absolute;
  top: -9px;
  right: -6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: $static-space-4;
  min-width: $static-space-4;
  padding: 0 $static-space-1;
  padding-top: 1px;
  font-size: $text-xs;
  line-height: 1;
  font-weight: $font-semibold;
  border-radius: $radius-full;
  background: var(--p-badge-warn-background);
  outline: $static-space-1 * 0.5 solid var(--p-surface-0);

  &--two-digits {
    font-size: $static-text-xs * 0.6667;
  }

  &--three-digits {
    font-size: $static-text-xs * 0.5;
  }
}

.p-menuitem-link.relative {
  position: relative;
  overflow: visible;
}

.app-nav__menu-item--active {
  color: var(--p-menu-item-focus-color);
  background: var(--p-menu-item-focus-background);
  border-radius: var(--p-border-radius-xl);
}
</style>
